import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { getOrdinal } from 'utils/common';

const HOLIDAY_TYPE_CONFIG = {
  EVENT_CELEBRATION: {
    label: 'Event Celebration',
    icon: 'PartyPopper',
    gradient: 'from-emerald-400 to-green-500',
    bg: 'bg-emerald-50/20 hover:bg-emerald-50/40',
    border: 'border-emerald-100/90 hover:border-emerald-200',
    text: 'text-emerald-700',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-700',
  },
  FULL_HOLIDAY: {
    label: 'Full Day',
    icon: 'Calendar',
    gradient: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50/20 hover:bg-violet-50/40',
    border: 'border-violet-100/90 hover:border-violet-200',
    text: 'text-violet-700',
    badgeBg: 'bg-violet-100',
    badgeText: 'text-violet-700',
  },
  HALF_HOLIDAY: {
    label: 'Half Day',
    icon: 'Clock',
    gradient: 'from-orange-400 to-amber-500',
    bg: 'bg-orange-50/20 hover:bg-orange-50/40',
    border: 'border-orange-100/90 hover:border-orange-200',
    text: 'text-orange-700',
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-700',
  },
};

// ===================== Avatar Component =====================
const Avatar = ({
  name,
  size = 40,
  gradient = 'from-indigo-500 to-purple-600',
  className = '',
}) => {
  const initials =
    name
      ?.split(' ')
      ?.map((n) => n[0])
      ?.join('')
      ?.slice(0, 2)
      ?.toUpperCase() || '';

  return (
    <div
      className={`rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold shrink-0 shadow-sm ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
};

// ===================== Event Card Component =====================
const EventCard = ({ event, eventType }) => {
  // Holiday card
  if (eventType === 'holiday') {
    const holidayType = event.type || 'EVENT_CELEBRATION';
    const config = HOLIDAY_TYPE_CONFIG[holidayType] || HOLIDAY_TYPE_CONFIG.EVENT_CELEBRATION;

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 3 }}
        animate={{ opacity: 1, y: 0 }}
        className={`group flex items-center gap-2.5 ${config.bg} border ${config.border} rounded-lg px-3 py-1.5 hover:shadow-sm transition-all duration-200`}
      >
        <Avatar name={event.title} size={28} gradient={config.gradient} />

        <div className="flex justify-between w-full text-xs min-w-0">
          <span className="font-semibold text-slate-800 truncate">{event.title}</span>
          <span
            className={`font-medium shrink-0 ml-2 px-1.5 py-0.5 rounded text-[10px] ${config.badgeBg} ${config.badgeText}`}
          >
            {config.label}
          </span>
        </div>
      </motion.div>
    );
  }

  // Bond Expiry card
  if (eventType === 'bond_expiry') {
    const displayName = `${event.first_name} ${event.last_name}`;

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 3 }}
        animate={{ opacity: 1, y: 0 }}
        className="group flex items-center gap-2.5 bg-red-50/20 hover:bg-red-50/40 border border-red-100/90 hover:border-red-200 rounded-lg px-3 py-1.5 hover:shadow-sm transition-all duration-200"
      >
        <Avatar name={displayName} size={28} gradient="from-rose-400 to-red-500" />

        <div className="flex justify-between w-full text-xs">
          <span className="font-semibold text-slate-800">{displayName}</span>
          <span className="font-medium text-red-700">Bond expires</span>
        </div>
      </motion.div>
    );
  }

  // Birthday / Anniversary card
  if (!['birthday', 'anniversary'].includes(eventType)) return null;

  const isBirthday = eventType === 'birthday';

  const targetDate = isBirthday ? event.next_birthday : event.next_anniversary;
  const eventYear = targetDate ? dayjs(targetDate).year() : dayjs().year();
  const years = isBirthday
    ? eventYear - dayjs(event.date_of_birth).year()
    : eventYear - dayjs(event.joining_date).year();

  const title = isBirthday
    ? `${getOrdinal(years)} Birthday`
    : `${getOrdinal(event.years_completed || event.years || years)} Year Anniversary`;

  const displayName = `${event.first_name} ${event.last_name}`;

  // Card theme configs based on event type
  const theme = isBirthday
    ? {
        cardBg: 'bg-amber-50/20 hover:bg-amber-50/40',
        border: 'border-amber-100/90 hover:border-amber-200',
        text: 'text-amber-700',
        avatarGradient: 'from-yellow-400 to-amber-500',
      }
    : {
        cardBg: 'bg-cyan-50/20 hover:bg-cyan-50/40',
        border: 'border-cyan-100/90 hover:border-cyan-200',
        text: 'text-cyan-700',
        avatarGradient: 'from-cyan-400 to-teal-500',
      };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 3 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group flex items-center gap-2.5 ${theme.cardBg} border ${theme.border} rounded-lg px-3 py-1.5 hover:shadow-sm transition-all duration-200`}
    >
      <Avatar name={displayName} size={28} gradient={theme.avatarGradient} />

      <div className="flex justify-between w-full text-xs">
        <span className="font-semibold text-slate-800">{displayName}</span>
        <span className={`font-medium ${theme.text}`}>{title}</span>
      </div>
    </motion.div>
  );
};

export default EventCard;
